import { ref } from 'vue'

import { HttpApiError } from '@backmarket/http-api/src/utils/HttpApiError'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import type { I18nDefinition } from '@backmarket/nuxt-module-i18n/types/i18n'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import type { GuestLinkPurpose } from '~/api/auth/types'
import { useGuestBmUserId } from '~/scopes/guest/composables/useGuestBmUserId'

type ResendGuestLinkTranslations = {
  successToastTitle: I18nDefinition
  successToastContent: I18nDefinition
}

export function useResendGuestLink(
  purpose: GuestLinkPurpose,
  translations: ResendGuestLinkTranslations,
  trackingZone: string,
) {
  const { bmUserId } = useGuestBmUserId()
  const $httpFetch = useHttpFetch()
  const { openErrorToast, openSuccessToast } = useTheToast()
  const { trackClick } = useTracking()
  const i18n = useI18n()
  const logger = useLogger()

  const isLoading = ref(false)
  const hasBeenSent = ref(false)

  async function handleResendLink() {
    trackClick({
      name: 'resend_guest_magic_link',
      zone: trackingZone,
    })

    if (!bmUserId.value) {
      logger.error('Missing bm_user_id query param when resending guest link')
      openErrorToast()

      return
    }

    isLoading.value = true

    try {
      await $httpFetch('/auth/v1/guests/:bmUserId/resend-link', {
        method: 'POST',
        pathParams: { bmUserId: bmUserId.value },
        body: { data: { purpose } },
      })

      openSuccessToast({
        title: i18n(translations.successToastTitle),
        content: i18n(translations.successToastContent),
      })

      hasBeenSent.value = true
    } catch (error) {
      const httpError = HttpApiError.fromAnyError(error)

      logger.error('Guest resend-link request failed', {
        error: httpError,
        owners: ['bot-squad-identity'],
      })

      openErrorToast()
    } finally {
      isLoading.value = false
    }
  }

  return { isLoading, hasBeenSent, handleResendLink }
}
